import React from 'react'
import { useTheme } from '@mui/material'
import HorizontalBarChart from './utils/HorizontalBarChart'
import { BarSeriesType } from '@mui/x-charts'
import {
    calculateIssueTotalOfferingSize,
    calculateSumSubscriptionCommitmentInPercentage,
    getCommitmentsInPercentage,
} from '../../pages/issues/utils'
import { roundFloat } from '../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { CommitChartWithInvestors } from './CommitChartWithInvestors'
import { IssueListItem, IssueSingleItem } from '@finance-lab/utils/types'

export function CommitChart({
    isLeftAlign,
    issue,
    size,
    skipAnimation,
}: {
    isLeftAlign?: boolean
    issue: Pick<
        IssueSingleItem | IssueListItem,
        | 'approxCommitment'
        | 'approxGuarantor'
        | 'noInvestorsNamed'
        | 'summarizedFeeLevels'
        | 'abbTarget'
        | 'investors'
        | 'secondaryOffering'
        | 'totalOfferedUnits'
        | 'approxTotalCommitment'
        | 'fees'
        | 'unitSubPrice'
    >
    size: 'small' | 'large'
    skipAnimation?: boolean
}) {
    const theme = useTheme()
    const { t } = useTranslation()
    let series: (Omit<BarSeriesType, 'type'> & { label: string; data: [number] })[] | null = null

    if (issue.investors.length > 0) {
        const { totalOfferingSize } = calculateIssueTotalOfferingSize(issue)
        const investorsExcludingSellers = issue.investors.filter((investor) => !investor.isSeller)
        const sumSubscriptionCommitmentInPercentage = calculateSumSubscriptionCommitmentInPercentage({
            investors: investorsExcludingSellers,
            totalOfferingSize,
        })

        const sumGarantCommitmentInPercentage = investorsExcludingSellers.reduce(
            (acc, investor) => (investor.feeLevel > 0 ? acc + (investor.amountInPercentage || 0) : acc),
            0,
        )
        const sumGarantCommitmentInCash = investorsExcludingSellers.reduce(
            (acc, investor) => (investor.feeLevel > 0 ? acc + (investor.amountInCash || 0) : acc),
            0,
        )

        const secondaryOffering = issue.secondaryOffering !== null ? issue.secondaryOffering : 0
        const totalOffered = (issue.totalOfferedUnits || 0) + secondaryOffering

        const totalSubscriptionCommitmentInPercentage = sumSubscriptionCommitmentInPercentage
        const totalGarantCommitmentInPercentage =
            sumGarantCommitmentInPercentage > 0
                ? sumGarantCommitmentInPercentage
                : (sumGarantCommitmentInCash * 100) / (totalOffered || 1)

        return (
            <CommitChartWithInvestors
                approxTotalCommitment={issue.approxTotalCommitment}
                fees={issue.fees}
                isLeftAlign={isLeftAlign}
                investors={issue.investors}
                size={size}
                skipAnimation={skipAnimation}
                summarizedFeeLevels={issue.summarizedFeeLevels}
                totalGuarantCommitmentInPercentage={totalGarantCommitmentInPercentage}
                totalSubscriptionCommitmentInPercentage={totalSubscriptionCommitmentInPercentage}
            />
        )
    }

    const commitments = getCommitmentsInPercentage(issue)

    if (!commitments) return null

    let { commitment, guarantor, totalCommitment, notAssured } = commitments

    if (commitment) {
        if (totalCommitment > 100) {
            // Normalize to 100%, so that the chart does not collapse
            commitment = (100 * commitment) / totalCommitment
            guarantor = (100 * (guarantor || 0)) / totalCommitment
            totalCommitment = 100
        }

        series = [
            {
                label: t('pages.issues.feeLevels.subscription'),
                data: [roundFloat(commitment, 4)],
                color: theme.palette.info.main,
            },
            {
                label: t('pages.issues.feeLevels.guarantorCommitment'),
                data: [guarantor ? roundFloat(guarantor, 4) : 0],
                color: theme.palette.info.light,
            },
            {
                label: t('pages.issues.feeLevels.notGuaranteed'),
                data: [roundFloat(notAssured, 4)],
                color: theme.palette.grey[200],
            },
        ]
    } else {
        series = [
            {
                label: t('pages.issues.feeLevels.totalCommitment'),
                data: [roundFloat(totalCommitment, 4)],
                color: theme.palette.info.main,
            },
            {
                label: t('pages.issues.feeLevels.notGuaranteed'),
                data: [roundFloat(notAssured, 4)],
                color: theme.palette.grey[200],
            },
        ]
    }

    return (
        <HorizontalBarChart
            isLeftAlign={isLeftAlign}
            labelValue={commitments.totalCommitment || 0}
            series={series}
            size={size}
            skipAnimation={skipAnimation}
        />
    )
}
